import { ReactNode, useEffect } from 'react';
import { useChat } from '@packages/shared/core/chat/hooks';
import { SECOND_IN_MS } from '@packages/shared/core/common/constants';
import { useLogger, useServices } from '@packages/shared/core/common/hooks';
import { delay } from '@packages/shared/core/common/utils/delay';
import { useExperts } from '@packages/shared/core/experts/hooks';
import {
  SimpleRegChatOnFunnel2ExperimentGroups,
  SimpleRegChatOnFunnel2ExperimentName,
} from '@packages/shared/core/feature-flags/config';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ChatStepData, FlowName, QuizScreenProps } from '@packages/shared/core/quiz/entities';

type ChatStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const ChatStep = ({ id, renderQuizScreen }: ChatStepProps) => {
  const logger = useLogger();
  const { analyticsService, featureFlags } = useServices();
  const { back, next, stepData } = useQuizStep(id);

  const { otherClickEvent } = stepData as ChatStepData;

  const { selectedExpert } = useExperts({
    flowName: FlowName.EXPERTS_CATALOGUE,
  });

  const expertId = selectedExpert ? selectedExpert.id : '';

  const {
    sendMessage,
    incomingMessage,
    outgoingMessage,
    receiveMessageRequest,
    hasChatSessionEverBeen,
    updateChatSession,
  } = useChat(expertId);

  const experiment = featureFlags.getExperimentGroup(SimpleRegChatOnFunnel2ExperimentName);
  const isExperiment =
    experiment === SimpleRegChatOnFunnel2ExperimentGroups.EXPERTS ||
    experiment === SimpleRegChatOnFunnel2ExperimentGroups.ONLY_MESSAGE;

  useEffect(() => {
    receiveMessageRequest();
  }, [receiveMessageRequest]);

  const doSendMessage = async ({ text }: { text: string }) => {
    sendMessage(text);
    if (isExperiment) {
      analyticsService.trackQuizEvent(otherClickEvent, { context: text });
    }

    await next(text);
    await delay(SECOND_IN_MS);

    updateChatSession();
  };

  const iceBreakingTipSelected = () => {
    analyticsService.trackQuizEvent(otherClickEvent, {});
  };

  if (!stepData) {
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goBack = async () => back();

  const onContinue = async () => {
    await next();
  };

  return (
    <>
      {renderQuizScreen({
        onContinue,
        onBack: goBack,
        incomingMessage,
        outgoingMessage,
        hasChatSessionEverBeen,
        iceBreakingTipSelected,
        sendMessage: doSendMessage,
        expert: selectedExpert,
        ...(stepData as ChatStepData),
      })}
    </>
  );
};

export default ChatStep;
